import { MobileManager } from './MobileManager';
import { VirtualJoystick } from '../ui/VirtualJoystick';
import { TouchFireZone } from '../ui/TouchFireZone';
import { TouchPauseButton } from '../ui/TouchPauseButton';

type KeyCallback = () => void;

export class InputManager {
  private keysPressed: Set<string> = new Set();
  private keyDownCallbacks: Map<string, KeyCallback[]> = new Map();
  private keyUpCallbacks: Map<string, KeyCallback[]> = new Map();

  private mousePosition = { x: 0, y: 0 };
  private mouseDelta = { x: 0, y: 0 };
  private mouseButtons: Set<number> = new Set();
  private isPointerLocked = false;

  // Touch controls
  private joystick: VirtualJoystick | null = null;
  private fireZone: TouchFireZone | null = null;
  private pauseButton: TouchPauseButton | null = null;
  private touchControlsInitialized = false;

  // Aiming: cumulative pitch from mouse Y movement or keyboard
  private aimPitch: number = 0;
  private readonly AIM_PITCH_MIN = -0.5; // ~-30 degrees (look down)
  private readonly AIM_PITCH_MAX = 0.8;  // ~45 degrees (look up at drones)
  private readonly AIM_SENSITIVITY = 0.002;
  private readonly AIM_KEYBOARD_SPEED = 1.5; // radians per second for keyboard aiming

  constructor() {
    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    // Keyboard events
    window.addEventListener('keydown', this.handleKeyDown);
    window.addEventListener('keyup', this.handleKeyUp);

    // Mouse events
    window.addEventListener('mousemove', this.handleMouseMove);
    window.addEventListener('mousedown', this.handleMouseDown);
    window.addEventListener('mouseup', this.handleMouseUp);

    // Pointer lock (only on non-touch devices)
    document.addEventListener('pointerlockchange', this.handlePointerLockChange);

    // Request pointer lock on click (skip on touch devices)
    window.addEventListener('click', () => {
      if (!this.isPointerLocked && !MobileManager.isTouchDevice()) {
        document.body.requestPointerLock();
      }
    });

    // Prevent context menu
    window.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  private handleKeyDown = (event: KeyboardEvent): void => {
    if (event.repeat) return;

    this.keysPressed.add(event.code);

    const callbacks = this.keyDownCallbacks.get(event.code);
    if (callbacks) {
      callbacks.forEach((cb) => cb());
    }
  };

  private handleKeyUp = (event: KeyboardEvent): void => {
    this.keysPressed.delete(event.code);

    const callbacks = this.keyUpCallbacks.get(event.code);
    if (callbacks) {
      callbacks.forEach((cb) => cb());
    }
  };

  private handleMouseMove = (event: MouseEvent): void => {
    if (this.isPointerLocked) {
      this.mouseDelta.x = event.movementX;
      this.mouseDelta.y = event.movementY;

      // Update aim pitch based on mouse Y movement (inverted: move up = aim up)
      this.aimPitch -= event.movementY * this.AIM_SENSITIVITY;
      this.aimPitch = Math.max(this.AIM_PITCH_MIN, Math.min(this.AIM_PITCH_MAX, this.aimPitch));
    } else {
      this.mousePosition.x = event.clientX;
      this.mousePosition.y = event.clientY;
    }
  };

  private handleMouseDown = (event: MouseEvent): void => {
    this.mouseButtons.add(event.button);
  };

  private handleMouseUp = (event: MouseEvent): void => {
    this.mouseButtons.delete(event.button);
  };

  private handlePointerLockChange = (): void => {
    this.isPointerLocked = document.pointerLockElement === document.body;
  };

  public isKeyPressed(code: string): boolean {
    return this.keysPressed.has(code);
  }

  public isMouseButtonPressed(button: number): boolean {
    return this.mouseButtons.has(button);
  }

  public getMousePosition(): { x: number; y: number } {
    return { ...this.mousePosition };
  }

  public getMouseDelta(): { x: number; y: number } {
    const delta = { ...this.mouseDelta };
    // Reset delta after reading
    this.mouseDelta.x = 0;
    this.mouseDelta.y = 0;
    return delta;
  }

  public hasPointerLock(): boolean {
    return this.isPointerLocked;
  }

  public getAimPitch(): number {
    return this.aimPitch;
  }

  public resetAimPitch(): void {
    this.aimPitch = 0;
  }

  // Update keyboard-based aiming (call each frame)
  public updateAiming(dt: number): void {
    // Q to aim up, E to aim down
    if (this.keysPressed.has('KeyQ')) {
      this.aimPitch += this.AIM_KEYBOARD_SPEED * dt;
    }
    if (this.keysPressed.has('KeyE')) {
      this.aimPitch -= this.AIM_KEYBOARD_SPEED * dt;
    }

    // Clamp pitch
    this.aimPitch = Math.max(this.AIM_PITCH_MIN, Math.min(this.AIM_PITCH_MAX, this.aimPitch));
  }

  public onKeyDown(code: string, callback: KeyCallback): void {
    if (!this.keyDownCallbacks.has(code)) {
      this.keyDownCallbacks.set(code, []);
    }
    this.keyDownCallbacks.get(code)!.push(callback);
  }

  public onKeyUp(code: string, callback: KeyCallback): void {
    if (!this.keyUpCallbacks.has(code)) {
      this.keyUpCallbacks.set(code, []);
    }
    this.keyUpCallbacks.get(code)!.push(callback);
  }

  // === Touch Control Methods ===

  /**
   * Initialize touch controls (joystick, fire zone, pause, fullscreen)
   * Call this after the game container is ready
   */
  public initTouchControls(container: HTMLElement, onPause?: () => void): void {
    if (this.touchControlsInitialized) return;
    if (!MobileManager.isTouchDevice()) return;

    this.joystick = new VirtualJoystick(container, 'left');
    this.fireZone = new TouchFireZone(container);

    // Add pause button if callback provided
    if (onPause) {
      this.pauseButton = new TouchPauseButton(container, onPause);
    }

    // Lock to landscape on mobile
    MobileManager.lockLandscape();

    this.touchControlsInitialized = true;
  }

  /**
   * Get touch movement values from virtual joystick
   * Returns { x: steer (-1 to 1), y: throttle (-1 to 1) }
   */
  public getTouchMovement(): { x: number; y: number } {
    if (!this.joystick) {
      return { x: 0, y: 0 };
    }
    return {
      x: this.joystick.getX(),
      y: this.joystick.getY(),
    };
  }

  /**
   * Check if player is firing via touch
   */
  public isTouchFiring(): boolean {
    return this.fireZone?.getIsFiring() ?? false;
  }

  /**
   * Check if touch controls are active
   */
  public hasTouchControls(): boolean {
    return this.touchControlsInitialized && MobileManager.isTouchDevice();
  }

  /**
   * Set callback for weapon switch (double-tap on fire zone)
   */
  public onTouchWeaponSwitch(callback: () => void): void {
    this.fireZone?.onWeaponSwitch(callback);
  }

  /**
   * Show/hide touch controls
   */
  public setTouchControlsVisible(visible: boolean): void {
    this.joystick?.setVisible(visible);
    this.fireZone?.setVisible(visible);
    this.pauseButton?.setVisible(visible);
  }

  /**
   * Destroy touch controls
   */
  public destroyTouchControls(): void {
    this.joystick?.destroy();
    this.fireZone?.destroy();
    this.pauseButton?.destroy();
    this.joystick = null;
    this.fireZone = null;
    this.pauseButton = null;
    this.touchControlsInitialized = false;

    MobileManager.unlockOrientation();
  }

  public destroy(): void {
    window.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('keyup', this.handleKeyUp);
    window.removeEventListener('mousemove', this.handleMouseMove);
    window.removeEventListener('mousedown', this.handleMouseDown);
    window.removeEventListener('mouseup', this.handleMouseUp);
    document.removeEventListener('pointerlockchange', this.handlePointerLockChange);

    // Clean up touch controls
    this.destroyTouchControls();
  }
}
